import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Grid } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { useNavigate } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';


import api from '../../utils/webcall';
import UserProfile from '../../components/User';
import { GreenTickIcon, RedCrossIcon } from '../../components/commons/icons';
import AccessController from '../../components/access_controller';


function ViewBillDetails(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const isMobile = useMediaQuery('(max-width:600px)');
    const [billData, setBillData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disableButton, setDisableButton] = useState(false);
    const [deletingModal, setDeletingModal] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const navigate = useNavigate();
    const username = UserProfile.getName();
    let bill_id = window.location.pathname.split("/");
    bill_id = bill_id[bill_id.length - 1];


    const loadBillDetails = () => {
        setLoading(true);
        api.get(`/pos/bill/details/${bill_id}`).then((res) => {
            let data = res.data;
            data['bills']['json_data'] = JSON.parse(data['bills']['json_data'])

            setBillData(data['bills']);
        }).catch((error) => {
            console.log(error);
            showErrorMessage(String(error));
        }).finally(() => {
            setLoading(false);
            setDisableButton(false);
        });
    }

    const approveBill = () => {
        setDisableButton(true);
        api.post(`/pos/bill/approve/${bill_id}/`).then((res) => {
            if (res.status === 200) {
                showSuccessMessage("Approved")
            }
            else {
                showErrorMessage(res.response.data);
            }
        }).catch((error) => {
            console.log(error);
            if (error.status === 400) {
                showErrorMessage(error.response.data);
            }
            else {
                showErrorMessage(error.message);
            }

        }).finally(() => {
            loadBillDetails();
        })
    }

    const deleteBill = () => {
        setDeletingModal(true);
        api.post(`/pos/bill/delete/${bill_id}/`).then((res) => {
            if (res.status === 200) {
                navigate("/bill/view");
            }
        }).catch((error) => {
            console.log(error);
            showErrorMessage(error.message);
        }).finally(() => {
            setDeletingModal(false);
        })
    }

    useEffect(() => {
        loadBillDetails();
    }, []);

    return (
        <AccessController
            onlyAdmin={false}
        >
            <Modal
                open={deletingModal}
                onClose={(e) => { }}
            >
                <CircularProgress />
            </Modal>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: isMobile ? '90%' : 400,
                        maxWidth: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 24,
                        p: isMobile ? 2 : 3,
                    }}
                >
                    <Grid
                        container
                        direction={'column'}
                        rowSpacing={2}
                        alignItems="center"
                    >
                        <Grid item>
                            <Alert severity="warning">
                                <Typography id="modal-modal-title" variant="h6" component="h2">
                                    Are you sure you want to delete this bill. <br />Once deleted cannot be undone.
                                </Typography>
                            </Alert>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                color="success"
                                sx={{
                                    margin: '10px'
                                }}
                                onClick={(e) => {
                                    setModalOpen(false);
                                }}
                            >
                                NO
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                sx={{
                                    margin: '10px'
                                }}
                                onClick={(e) => deleteBill()}
                            >
                                YES
                            </Button>

                        </Grid>
                    </Grid>
                </Box>
            </Modal>
            <Grid
                container
                direction='column'
                spacing={2}
                alignItems='center'
                sx={{ px: { xs: 1, sm: 3 }, py: 2 }}
            >
                <Grid item xs={12}>
                    <Typography variant='h5' gutterBottom>DIVASH | ADMIN</Typography>
                    <Typography variant='subtitle1' gutterBottom>Welcome {username}</Typography>
                    <Typography variant='h6' gutterBottom>View Bill</Typography>
                </Grid>

                <Grid item container spacing={2} justifyContent='center'>
                    {UserProfile.isManager() && (
                        <Grid item xs={12} sm='auto'>
                            <Button
                                variant='outlined'
                                fullWidth={isMobile}
                                onClick={approveBill}
                                disabled={disableButton}
                            >
                                Approve
                            </Button>
                        </Grid>
                    )}
                    {!loading && billData && !billData['approved'] && (
                        <Grid item xs={12} sm='auto'>
                            <Button
                                variant='outlined'
                                fullWidth={isMobile}
                                onClick={() => setModalOpen(true)}
                                disabled={disableButton}
                            >
                                Delete Bill
                            </Button>
                        </Grid>
                    )}
                </Grid>
                <Grid item sx={{ width: '100%' }}>
                    {loading ? (
                        <Grid container justifyContent='center' alignItems='center' sx={{ minHeight: 120 }}>
                            <CircularProgress />
                        </Grid>
                    ) : billData && (
                        isMobile ? (
                            <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                                <Typography variant='subtitle2' color='text.secondary'>Bill #{bill_id}</Typography>
                                <Typography variant='body1'><strong>Status:</strong> {billData['approved'] ? <GreenTickIcon /> : <RedCrossIcon />}</Typography>
                                <Typography variant='body1'><strong>Date:</strong> {billData['submitted_date']}</Typography>
                                <Typography variant='body1'><strong>Amount:</strong> {billData['total_amount'].toFixed(2)} {billData['currency']}</Typography>
                                <Typography variant='body1'><strong>Items:</strong> {billData['number_of_items']}</Typography>
                                <Typography variant='body1'><strong>By:</strong> {billData['user'] ? `${billData['user']['username']} ${billData['user']['last_name']}` : ''}</Typography>
                                {billData['json_data']['saved_bills_path'].map((path, index) => {
                                    console.log("path", path);
                                    return (
                                        <Box key={`bill-image-box-${index}`} sx={{ mt: 2 }}>
                                            <img
                                                src={path} alt={`Bill Image ${index + 1}`} style={{ maxWidth: '100%', borderRadius: 4 }} />
                                        </Box>
                                    )
                                })}
                            </Paper>
                        ) : (
                            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Bill #</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Amount</TableCell>
                                            <TableCell>Items</TableCell>
                                            <TableCell>Submitted By</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>{bill_id}</TableCell>
                                            <TableCell>{billData['approved'] ? <GreenTickIcon /> : <RedCrossIcon />}</TableCell>
                                            <TableCell>{billData['submitted_date']}</TableCell>
                                            <TableCell>{billData['total_amount'].toFixed(2)} {billData['currency']}</TableCell>
                                            <TableCell>{billData['number_of_items']}</TableCell>
                                            <TableCell>{billData['user'] ? `${billData['user']['first_name']} ${billData['user']['last_name']}` : ''}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                        )
                    )}
                </Grid>
                {!loading && billData && billData['json_data'] && billData['json_data']['data']?.length > 0 && (
                    <Grid item sx={{ width: '100%' }}>
                        <Typography variant='h6' gutterBottom>Bill Details</Typography>
                        {isMobile ? (
                            <Grid container spacing={2}>
                                {billData['json_data']['data'].map((item, index) => (
                                    <Grid item xs={12} key={`bill-item-${index}`}>
                                        <Paper variant='outlined' sx={{ p: 2 }}>
                                            <Typography variant='body1'><strong>Item:</strong> {item['item_name']}</Typography>
                                            <Typography variant='body1'><strong>Quantity:</strong> {item['quantity']} {item['measurement_unit']}</Typography>
                                            <Typography variant='body1'><strong>Price:</strong> {item['total_price']} {billData['currency']}</Typography>
                                            <Typography variant='body1'><strong>Per Unit:</strong> {Math.round(100 * (parseFloat(item['total_price']) / parseFloat(item['quantity']))) / 1000} per {item['measurement_unit']}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <TableContainer component={Paper}>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Item Name</TableCell>
                                            <TableCell>Quantity</TableCell>
                                            <TableCell>Total Price</TableCell>
                                            <TableCell>Price Per Unit</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {billData['json_data']['data'].map((item, index) => (
                                            <TableRow key={`bill-item-${index}`}>
                                                <TableCell>{item['item_name']}</TableCell>
                                                <TableCell>{item['quantity']} {item['measurement_unit']}</TableCell>
                                                <TableCell>{item['total_price']} {billData['currency']}</TableCell>
                                                <TableCell>{Math.round(100 * (parseFloat(item['total_price']) / parseFloat(item['quantity']))) / 1000} per {item['measurement_unit']}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Grid>
                )}
            </Grid>
        </AccessController >
    )
}

export default ViewBillDetails;